import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, BedDouble, Compass, Fish, Headphones, Landmark, ShieldCheck, Ship, Sparkles, Sunset, Trees, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSiteData } from "@/context/SiteDataContext";
import { PackageCard } from "@/components/site/PackageCard";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { GuestStories } from "@/components/site/GuestStories";
import { TravelServices } from "@/components/site/TravelServices";
import { HomeWaterActivities } from "@/components/site/WaterActivities";
import { HomeSocial } from "@/components/site/Social";
import { HomeFaq, faqJsonLd } from "@/components/site/HomeFaq";
import { Seo } from "@/components/site/Seo";
import { CATEGORIES, CATEGORY_INFO, DESTINATIONS, DURATIONS, IMG, LANDING_BY_CATEGORY } from "@/lib/constants";
import { inr, waLink } from "@/lib/api";

const Hero = () => {
  const { settings } = useSiteData();
  const navigate = useNavigate();
  const [duration, setDuration] = useState("all");
  const [category, setCategory] = useState("all");
  const search = () => {
    const p = new URLSearchParams();
    if (duration !== "all") p.set("duration", duration);
    if (category !== "all") p.set("category", category);
    navigate(`/packages?${p.toString()}`);
  };
  return (
    <section className="relative min-h-[88vh] flex items-end md:items-center overflow-hidden" data-testid="home-hero">
      <img src={IMG.hero} alt="Palm-lined white sand beach in the Andaman Islands" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/90 via-[#0A192F]/55 to-[#0A192F]/10" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A192F]/60 to-transparent" />
      <div className="relative container-x w-full pt-24 pb-12 md:py-24">
        <div className="max-w-2xl">
          <p className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-aqua">Andaman &amp; Nicobar Islands Specialists</p>
          <h1 className="reveal mt-5 font-display text-4xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.05] tracking-tight" style={{ animationDelay: "120ms" }}>
            Your Andaman holiday, <span className="text-aqua">expertly planned.</span>
          </h1>
          <p className="reveal mt-6 text-base md:text-lg text-slate-100/90 max-w-xl leading-relaxed" style={{ animationDelay: "240ms" }}>
            Tour packages, ferry tickets and hotel stays across Port Blair, Havelock (Swaraj Dweep), Neil (Shaheed Dweep) and Baratang, all arranged by Ocean Wings Travel.
          </p>
          <div className="reveal mt-8 flex flex-wrap gap-3" style={{ animationDelay: "360ms" }}>
            <Button asChild className="h-12 px-7 rounded-full bg-white text-navy hover:bg-aqua hover:text-navy text-base font-semibold" data-testid="hero-explore-button">
              <Link to="/packages">Explore Packages <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, please help me plan an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors">
              <WhatsAppIcon className="h-5 w-5" /> Enquire on WhatsApp
            </a>
          </div>
          <div className="reveal mt-5 flex flex-wrap gap-2" style={{ animationDelay: "420ms" }}>
            <Link to="/ferry-booking" data-testid="hero-ferry-link" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-sm font-semibold hover:bg-white hover:text-navy transition-colors"><Ship className="h-4 w-4" />Ferry Booking</Link>
            <Link to="/hotel-booking" data-testid="hero-hotel-link" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-sm font-semibold hover:bg-white hover:text-navy transition-colors"><BedDouble className="h-4 w-4" />Hotel Booking</Link>
            <Link to="/water-activities" data-testid="hero-water-link" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-sm font-semibold hover:bg-white hover:text-navy transition-colors"><Waves className="h-4 w-4" />Water Activities</Link>
          </div>
        </div>
        <div className="reveal mt-12 max-w-3xl rounded-2xl bg-white/95 backdrop-blur-xl p-4 sm:p-5 shadow-2xl grid gap-3 sm:grid-cols-[1fr_1fr_auto]" style={{ animationDelay: "480ms" }}>
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger className="h-12" data-testid="hero-duration-select"><SelectValue placeholder="Duration" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Any duration</SelectItem>{DURATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12" data-testid="hero-category-select"><SelectValue placeholder="Package type" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Any package type</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={search} className="h-12 px-8 rounded-xl bg-ocean hover:bg-navy font-semibold" data-testid="hero-search-button">Find Packages</Button>
        </div>
      </div>
    </section>
  );
};

const Durations = ({ packages }) => (
  <section className="container-x pt-24" data-testid="home-durations">
    <p className="eyebrow">Choose your trip length</p>
    <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">From quick escapes to the full island circuit</h2>
    <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
      {DURATIONS.map((d, i) => {
        const prices = packages.filter((p) => p.duration === d).map((p) => p.selling_price);
        return (
          <Link key={d} to={`/packages?duration=${d}`} data-testid={`home-duration-${d}`}
            className={`group rounded-2xl border p-6 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lg ${i === 2 ? "bg-ocean text-white border-ocean" : "bg-white border-slate-200 hover:border-sky-300"}`}>
            <p className={`font-mono text-3xl font-semibold ${i === 2 ? "text-white" : "text-navy"}`}>{d}</p>
            <p className={`mt-1 text-sm ${i === 2 ? "text-sky-100" : "text-slate-500"}`}>{d.split("/")[0].replace("N", "")} nights · {d.split("/")[1].replace("D", "")} days</p>
            {prices.length > 0 && <p className={`mt-6 text-xs ${i === 2 ? "text-sky-100" : "text-slate-500"}`}>from <span className={`font-mono text-base font-semibold ${i === 2 ? "text-white" : "text-ocean"}`}>{inr(Math.min(...prices))}</span></p>}
          </Link>
        );
      })}
    </div>
  </section>
);

const Categories = () => (
  <section className="container-x pt-24" data-testid="home-categories">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div>
        <p className="eyebrow">Packages for every traveller</p>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">Pick your style of island holiday</h2>
      </div>
      <Link to="/packages" className="text-sm font-semibold text-ocean hover:text-navy inline-flex items-center gap-1" data-testid="home-all-packages-link">View all packages <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 md:auto-rows-[220px]">
      {CATEGORIES.map((c, i) => {
        const span = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"][i];
        return (
          <Link key={c} to={LANDING_BY_CATEGORY[c] || `/packages?category=${c}`} data-testid={`home-category-${c.toLowerCase()}`}
            className={`group relative overflow-hidden rounded-2xl min-h-[220px] ${span}`}>
            <img src={CATEGORY_INFO[c].img} alt={`${c} Andaman package`} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/85 via-[#0A192F]/20 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <p className="font-display text-3xl text-white font-semibold">{c}</p>
              <p className="text-sm text-slate-200 mt-1">{CATEGORY_INFO[c].blurb}</p>
            </div>
          </Link>
        );
      })}
    </div>
  </section>
);

const Featured = ({ packages }) => {
  const featured = useMemo(() => {
    const picks = CATEGORIES.map((c) => packages.find((p) => p.category === c && p.duration === "4N/5D") || packages.find((p) => p.category === c));
    return [...packages.filter((p) => p.featured), ...picks].filter((p, i, a) => p && a.indexOf(p) === i);
  }, [packages]);
  if (!featured.length) return null;
  return (
    <section className="bg-mist mt-24 py-24" data-testid="home-featured">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Most-loved itineraries</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">Popular Andaman Packages</h2>
            <p className="mt-3 text-slate-600">Our most-loved Andaman itineraries in every style, from budget to luxury.</p>
          </div>
          <Link to="/packages" className="text-sm font-semibold text-ocean hover:text-navy inline-flex items-center gap-1" data-testid="home-popular-all-link">View all packages <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p) => <PackageCard key={p.id} pkg={p} />)}
        </div>
      </div>
    </section>
  );
};

const EXPERIENCES = [
  { icon: Fish, t: "Snorkelling & scuba diving" }, { icon: Sunset, t: "Sunset beaches" }, { icon: Ship, t: "Island-hopping ferries" },
  { icon: Landmark, t: "Cellular Jail Light & Sound Show" }, { icon: Trees, t: "Mangrove creeks & limestone caves" }, { icon: Waves, t: "Glass-bottom boat & sea walk" },
];

const Destinations = () => (
  <section className="container-x pt-24" data-testid="home-destinations">
    <p className="eyebrow">Destinations &amp; experiences</p>
    <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">The Andaman circuit, island by island</h2>
    <div className="mt-8 flex flex-wrap gap-2" data-testid="home-experiences">
      {EXPERIENCES.map(({ icon: Icon, t }) => <span key={t} className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-mist px-4 py-2 text-sm font-medium text-navy"><Icon className="h-4 w-4 text-ocean" />{t}</span>)}
    </div>
    <div className="mt-10 flex gap-5 overflow-x-auto no-scrollbar snap-x pb-2">
      {DESTINATIONS.map((d) => (
        <div key={d.name} className="snap-start shrink-0 w-[260px] sm:w-[300px] group">
          <div className="aspect-[3/4] rounded-2xl overflow-hidden">
            <img src={d.img} alt={d.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
          <p className="mt-4 font-display text-2xl text-navy font-semibold">{d.name}</p>
          <p className="text-sm text-slate-600 mt-1">{d.note}</p>
        </div>
      ))}
    </div>
  </section>
);

const WHY = [
  { icon: Compass, t: "Andaman-only focus", d: "We plan only Andaman & Nicobar trips, so every itinerary is built around real ferry and sightseeing timings." },
  { icon: Sparkles, t: "Fully customisable", d: "Change hotels, add a day in Havelock or include scuba — we tailor every package to you." },
  { icon: ShieldCheck, t: "Clear, itemised quotes", d: "Day-wise itinerary and transparent inclusions in a professional PDF quotation." },
  { icon: Headphones, t: "WhatsApp support", d: "Quick answers before and during your trip on WhatsApp." },
];

const Why = () => (
  <section className="container-x pt-24" data-testid="home-why">
    <div className="grid gap-12 lg:grid-cols-12 items-center">
      <div className="lg:col-span-5 relative">
        <img src={IMG.scuba} alt="Scuba diving over coral reef" loading="lazy" className="rounded-3xl aspect-[4/5] w-full object-cover" />
        <img src={IMG.lagoon} alt="Turquoise lagoon" loading="lazy" className="hidden sm:block absolute -bottom-8 -right-6 w-44 h-44 rounded-2xl object-cover border-8 border-white shadow-xl" />
      </div>
      <div className="lg:col-span-7">
        <p className="eyebrow">Island experts, from first message to final ferry</p>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy font-semibold">Why Choose Ocean Wings Travel</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-8">
          {WHY.map(({ icon: Icon, t, d }) => (
            <div key={t}>
              <span className="h-11 w-11 rounded-xl bg-sky-50 text-ocean grid place-items-center"><Icon className="h-5 w-5" /></span>
              <p className="mt-4 font-semibold text-navy">{t}</p>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const EnquirySection = () => {
  const { settings } = useSiteData();
  return (
    <section className="container-x pt-28" id="enquire" data-testid="home-enquiry-section">
      <div className="rounded-3xl bg-navy p-6 sm:p-10 lg:p-14 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5 text-white">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-aqua">Free trip planning</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold">Tell us your dates. We'll do the rest.</h2>
          <p className="mt-5 text-slate-300">Share your travel date, group size and preferred package — we'll reply with a detailed day-wise quotation.</p>
          <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I want a quote for an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="enquiry-section-whatsapp-button"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-emerald-500 font-semibold hover:bg-emerald-600 transition-colors">
            <WhatsAppIcon className="h-5 w-5" /> WhatsApp {settings.whatsapp}
          </a>
        </div>
        <div className="lg:col-span-7 rounded-2xl bg-white p-6 sm:p-8">
          <EnquiryForm idPrefix="home-enquiry" />
        </div>
      </div>
    </section>
  );
};

const WhatsAppBand = () => {
  const { settings } = useSiteData();
  return (
    <section className="container-x pt-16" data-testid="home-whatsapp-cta">
      <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-emerald-500 p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6 text-white">
        <div className="flex items-center gap-5">
          <span className="hidden sm:grid h-16 w-16 shrink-0 rounded-2xl bg-white/15 place-items-center"><WhatsAppIcon className="h-8 w-8" /></span>
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold">Plan your trip on WhatsApp</h2>
            <p className="mt-2 text-emerald-50">Packages, ferries or hotels: message Ocean Wings Travel on {settings.whatsapp} for a quick reply.</p>
          </div>
        </div>
        <a href={waLink(settings.whatsapp, "Hi Ocean Wings Travel, I'd like to plan an Andaman trip.")} target="_blank" rel="noreferrer" data-testid="home-whatsapp-cta-button"
          className="shrink-0 inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white text-emerald-700 text-base font-bold hover:bg-emerald-50 transition-colors">
          <WhatsAppIcon className="h-5 w-5" /> Enquire on WhatsApp
        </a>
      </div>
    </section>
  );
};

export default function Home() {
  const { packages } = useSiteData();
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => { document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 150);
    }
  }, [hash]);
  return (
    <div data-testid="home-page">
      <Seo title="Andaman Tour Packages | Port Blair, Havelock & Neil Island | Ocean Wings Travel"
        description="Andaman & Nicobar tour packages by Ocean Wings Travel: Port Blair, Havelock, Neil Island, Baratang & Ross Island. Honeymoon, family & luxury trips with ferry and hotel booking."
        path="/" jsonLd={[faqJsonLd]} />
      <Hero />
      <TravelServices />
      <HomeWaterActivities />
      <Why />
      <Featured packages={packages} />
      <Categories />
      <Durations packages={packages} />
      <Destinations />
      <GuestStories />
      <HomeFaq />
      <EnquirySection />
      <HomeSocial />
      <WhatsAppBand />
    </div>
  );
}
